import React, { useState } from 'react';
import { Drawer } from '../common/Drawer';
import { StatusBadge, ChannelBadge, ScoreBadge } from '../common/Badge';
import { formatDate } from '../../utils/formatters';
import { ConversationPreview } from './ConversationPreview';

export function LeadDrawer({ lead, isOpen, onClose, onAddNote, onUpdateStatus }) {
  const [noteText, setNoteText] = useState('');

  if (!lead) return null;

  const handleAddNote = (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    onAddNote(lead.id, noteText, "JLL Leasing Team");
    setNoteText('');
  };

  const getNextAction = (status) => {
    switch (status) {
      case 'new': return 'Contact lead via phone within 15 minutes.';
      case 'qualified': return 'Schedule site visit viewing of office spaces.';
      case 'contacted': return 'Prepare commercial leasing proposal.';
      case 'site_visit': return 'Issue draft lease agreement with term sheet.';
      case 'proposal': return 'Follow up for contract execution & security deposit.';
      case 'leased': return 'Hand over space keys and finalize onboarding.';
      default: return 'Follow up with lead on requirements.';
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={`Lead Details — ${lead.id}`} width="600px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header */}
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{lead.id}</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-main)', marginTop: '2px' }}>{lead.company || lead.name}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>Contact: {lead.name}</div>
          </div>
          <div>
            <StatusBadge status={lead.status} />
          </div>
        </div>

        {/* Section 1: Overview */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '8px' }}>Overview</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '12.5px' }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Phone: </span>
              <span style={{ fontWeight: 500 }}>{lead.phone || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Email: </span>
              <span style={{ fontWeight: 500 }}>{lead.email || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>LINE ID: </span>
              <span style={{ fontWeight: 500 }}>{lead.lineId || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Channel: </span>
              <ChannelBadge channel={lead.channel} />
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Assigned Agent: </span>
              <span style={{ fontWeight: 500 }}>{lead.assignedTo || 'Unassigned'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Last Activity: </span>
              <span style={{ color: 'var(--text-muted)' }}>{formatDate(lead.lastActivity)}</span>
            </div>
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: 'var(--border-color)' }}></div>

        {/* Section 2: Qualification */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '8px' }}>Qualification</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '12.5px' }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Lead Score: </span>
              <ScoreBadge score={lead.score} />
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Intent: </span>
              <span style={{ fontWeight: 500 }}>{lead.intent || 'High'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Property Type: </span>
              <span style={{ fontWeight: 500 }}>{lead.propertyType || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Business Type: </span>
              <span style={{ fontWeight: 500 }}>{lead.businessType || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Space Required: </span>
              <span style={{ fontWeight: 500 }}>{lead.space || '-'}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Timeline: </span>
              <span style={{ fontWeight: 500 }}>{lead.timeline || '-'}</span>
            </div>
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: 'var(--border-color)' }}></div>

        {/* Section 3: Next Action */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '4px' }}>Recommended Next Action</div>
          <div style={{ fontSize: '12.5px', color: 'var(--text-main)', backgroundColor: '#F9FAFB', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
            {getNextAction(lead.status)}
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: 'var(--border-color)' }}></div>

        {/* Section 4: Conversation */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '8px' }}>Conversation Transcript</div>
          <ConversationPreview conversation={lead.conversation} />
        </div>

        <div style={{ height: '1px', backgroundColor: 'var(--border-color)' }}></div>

        {/* Section 5: Activity & Notes */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '8px' }}>Activity & Notes</div>
          <form onSubmit={handleAddNote} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add note or update log..."
              style={{ flex: 1, height: '36px' }}
            />
            <button
              type="submit"
              style={{
                height: '36px',
                padding: '0 12px',
                backgroundColor: '#168C82',
                color: '#FFF',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Add Note
            </button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lead.notes && lead.notes.length > 0 ? (
              lead.notes.map((note) => (
                <div key={note.id} style={{ fontSize: '12px', padding: '8px 10px', backgroundColor: '#F9FAFB', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '2px' }}>
                    {note.author} • {formatDate(note.date)}
                  </div>
                  <div>{note.text}</div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No notes added yet.</div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
